/* Sekme 1 — açılış + günün programı. Cihaz saatine göre "şu an" vurgusu. */

import type { Lang, SiteContent } from "../../shared/types.js";
import { t } from "../../shared/types.js";
import { ui } from "../lib/i18n.js";
import { toMin, useClock } from "../lib/clock.js";

export function Program({ content, lang }: { content: SiteContent; lang: Lang }) {
  const now = useClock();
  const { event, sessions, speakers } = content;

  const end = (s: { end: string }) => toMin(s.end === "24:00" ? "23:59" : s.end);
  const currentId = sessions.find((s) => now >= toMin(s.start) && now < end(s))?.id;
  const nextId = currentId
    ? undefined
    : sessions.find((s) => toMin(s.start) > now)?.id;

  const nameOf = (id: string) => speakers.find((sp) => sp.id === id)?.name;

  return (
    <div>
      <header className="center logo-in" style={{ padding: "30px 0 30px" }}>
        <h1 style={{ margin: 0 }}>
          <img
            src="/gorseller/logo.png"
            alt={event.name + " — Change the World"}
            width={760}
            height={568}
            fetchPriority="high"
            style={{ width: "min(300px, 78%)", height: "auto", margin: "0 auto" }}
          />
        </h1>
        <p style={{ margin: "14px 0 0", color: "var(--text-soft)", fontSize: 15.5 }}>
          {t(event.dateLabel, lang)} · {t(event.venue, lang)}
        </p>
        <p
          style={{
            margin: "10px 0 0",
            color: "var(--gold)",
            fontSize: 15,
            fontStyle: "italic",
          }}
        >
          {t(event.tagline, lang)}
        </p>
      </header>

      <h2 className="eyebrow" style={{ margin: "0 0 16px" }}>
        {ui("programTitle", lang)}
      </h2>

      <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {sessions.map((s, i) => {
          const isNow = s.id === currentId;
          const isNext = s.id === nextId;
          const isPast = now >= end(s);
          const names = s.speakerIds.map(nameOf).filter(Boolean).join(" · ");

          return (
            <li
              key={s.id}
              className={
                "tl rise" + (isNow ? " tl__item--now" : "") + (isPast ? " tl__item--past" : "")
              }
              style={{ ["--i" as string]: Math.min(i, 8) }}
            >
              <div className="tl__time">
                <div className="serif tnum" style={{ fontSize: 19, lineHeight: 1.1 }}>
                  {s.start}
                </div>
                <div className="tnum dim" style={{ fontSize: 13, marginTop: 3 }}>
                  {s.end}
                </div>
              </div>

              <div className="tl__spine" aria-hidden="true">
                <span className={"tl__dot" + (isNow ? " tl__dot--now" : "")} />
              </div>

              <div className="tl__body">
                {(isNow || isNext) && (
                  <span
                    className={"chip chip--ui " + (isNow ? "chip--now" : "chip--gold")}
                    style={{ marginBottom: 8 }}
                  >
                    {ui(isNow ? "programNow" : "programNext", lang)}
                  </span>
                )}
                <h3 className="serif" style={{ fontSize: 23, lineHeight: 1.2, margin: 0 }}>
                  {t(s.title, lang)}
                </h3>
                {names && (
                  <p style={{ margin: "6px 0 0", color: "var(--gold)", fontSize: 15 }}>{names}</p>
                )}
                {t(s.sub, lang) && (
                  <p className="muted" style={{ margin: "6px 0 0", fontSize: 15 }}>
                    {t(s.sub, lang)}
                  </p>
                )}
                {t(s.loc, lang) && (
                  <p className="dim" style={{ margin: "6px 0 0", fontSize: 14.5 }}>
                    {t(s.loc, lang)}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {event.practical.length > 0 && (
        <section style={{ marginTop: 34 }}>
          <h2 className="eyebrow" style={{ margin: "0 0 14px" }}>
            {ui("practical", lang)}
          </h2>
          <div className="card stack" style={{ gap: 14 }}>
            {event.practical.map((row, i) => (
              <div key={i}>
                <div
                  style={{
                    fontSize: 12,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: "var(--label)",
                    fontWeight: 600,
                  }}
                >
                  {t(row.k, lang)}
                </div>
                <div className="muted" style={{ marginTop: 4 }}>
                  {t(row.v, lang)}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
